import * as React from 'react';
import { withRouter, BrowserHistory } from "react-router-dom";
import { observable, action, runInAction, computed } from 'mobx';
import { observer } from 'mobx-react';
import { IOrganisation, IRole, IGroup } from '../../model/ModelTypes';
import { IApi, RegisterRequest } from '../../utility/IApi';
import { CircularProgress, Button, TextField, SelectField, Card, CardText, 
    CardTitle, CardActions, Snackbar } from 'react-md';
import { isNullOrUndefined } from 'util';
import { IInputValidator } from '../../utility/IInputValidator';

export class RegisterModel {
    @observable parentOrganisations: IOrganisation[] = [];
    @observable childOrganisations: IOrganisation[] = [];
    @observable selectedParentOrganisation: IOrganisation;
    @observable selectedChildOrganisation: IOrganisation;
    @observable roles: IRole[] = [];
    @observable groups: IGroup[] = [];
    @observable typedUsername: string = "";
    @observable typedPassword: string = "";
    @observable typedConfirmPassword: string = "";
    @observable typedAccessCode: string = "";
    @observable typedEmail: string = "";
    @observable typedName: string = "";
    @observable selectedRoleId: string = "";
    @observable selectedGroupId: string = "";
    @observable asyncOperationsCount = 0;
    @observable errorMessages: string[] = [];
    @observable rolesDropdownDisabled: boolean = true;
    @observable groupsDropdownDisabled: boolean = true;
    @observable usernameError: boolean = false;
    @observable emailError: boolean = false;
    @observable nameError: boolean = false;
    @observable passwordError: boolean = false;
    @observable confirmPasswordError: boolean = false;
    @observable accessCodeError: boolean = false;
    @observable registerationCompleted: boolean = false;
    @computed get hideRoles() {
        return this.roles.length === 0 && this.rolesDropdownDisabled === false;
    }
    @computed get hideGroups() {
        return this.groups.length === 0 && this.groupsDropdownDisabled === false;
    }
    @computed get selectedRole() {
        return this.roles.find(item => item.id === this.selectedRoleId);
    }
    private organisationsList: IOrganisation[];
    private api: IApi; 
    private validator: IInputValidator;
    constructor(api: IApi, validator: IInputValidator) {
        this.api = api;
        this.validator = validator;
    }
    @computed get registerDisabled() {
        return (
            this.emailError || this.usernameError || this.accessCodeError || this.nameError 
            || this.passwordError || this.confirmPasswordError || this.selectedRoleId === "" 
            || this.typedName.length === 0 || this.typedConfirmPassword.length === 0
            || this.typedPassword.length === 0 || this.typedUsername.length === 0
        );
    }
    @action fetchOrganisations() {
        this.asyncOperationsCount = this.asyncOperationsCount + 1;
        this.api.getAllOrganisation().subscribe( 
            (result) => {
                this.organisationsList = result;
                runInAction( () => {
                    this.parentOrganisations = this.organisationsList.filter(
                        item => item.id !== "1" && item.id !== "2"  
                                && (item.parent_id === "null" || item.parent_id === "1" || item.parent_id === "2")
                    ).sort((a, b) => a.name.localeCompare(b.name));
                    this.asyncOperationsCount = this.asyncOperationsCount - 1;
                    this.selectedParentOrganisation = this.parentOrganisations[0];
                    this.onParentOrganisationSelected(this.selectedParentOrganisation.code);
                });
            },
            (error) => {
                runInAction( () => {
                    this.errorMessages.splice(0, this.errorMessages.length);
                    this.errorMessages.push(error.message);
                    this.asyncOperationsCount = this.asyncOperationsCount - 1;
                });
            });
    }

    @action onParentOrganisationSelected(orgCode: string) {
        let orgIndex = this.organisationsList.findIndex(item => item.code === orgCode);
        if (orgIndex > -1) {
            let parentOrganisation = this.organisationsList[orgIndex];
            this.selectedParentOrganisation = parentOrganisation;
            this.childOrganisations = this.organisationsList
            .filter(item => item.parent_id === parentOrganisation.id)
            .sort((a, b) => a.name.localeCompare(b.name));
            this.fetchOrganisationDetails();
            
        }
    }

    @action onChildOrganisationSelected(orgCode: string) {
        let orgIndex = this.organisationsList.findIndex(item => item.code === orgCode);
        if (orgIndex > -1) {
            let childOrganisation = this.organisationsList[orgIndex];
            this.selectedChildOrganisation = childOrganisation;
            this.fetchOrganisationDetails();
        }
    }

    @action.bound fetchOrganisationDetails() {
        let organisationCode = 
        isNullOrUndefined(this.selectedChildOrganisation) ? this.selectedParentOrganisation.code
        : this.selectedChildOrganisation.code;
        this.api.getOrganisationDetails(organisationCode).subscribe( 
            organisation => {
                runInAction( () => {
                    this.roles = isNullOrUndefined(organisation.roles) ? [] : organisation.roles;
                    this.groups = isNullOrUndefined(organisation.groups) ? [] : organisation.groups;
                    this.rolesDropdownDisabled = false;
                    this.groupsDropdownDisabled = false;
                });
            },
            error => {
                this.errorMessages.splice(0, this.errorMessages.length);
                this.errorMessages.push(error.message);
            });
    }
    @action onTextFieldChange(key: string, value: string) {
        switch (key) {
            case "username":
                this.typedUsername = value;
                this.usernameError = !this.validator.isValidUserName(value);
                break;
            case "password":
                this.typedPassword = value;
                this.passwordError = !this.validator.isValidPassword(value);
                break;
            case "confirmPassword":
                this.typedConfirmPassword = value;
                this.confirmPasswordError = !this.validator.isValidPassword(value) || 
                value !== this.typedPassword ;
                break;
            case "name":
                this.typedName = value;
                this.nameError = !this.validator.isValidName(value);
                break;
            case "email":
                this.typedEmail = value;
                if (
                    isNullOrUndefined(this.selectedRole) || 
                    isNullOrUndefined(this.selectedRole.emailOptional) || 
                    !this.selectedRole.emailOptional || 
                    this.selectedRole.emailOptional && value !== ""
                ) {
                    this.emailError = !this.validator.isValidEmail(value);
                } else {
                    this.emailError = false;
                }
                break;
            case "accessCode":
                this.typedAccessCode = value;
                break;
            default:
                return;
        }
        
    }
    @action.bound incrementAsyncOperations() {
        this.asyncOperationsCount = this.asyncOperationsCount + 1;
    }
    @action.bound decrementAsyncOperations() {
        this.asyncOperationsCount = this.asyncOperationsCount - 1;
    }
    @action onRegisterClicked() {
        this.incrementAsyncOperations();
        let organisationCode = 
        isNullOrUndefined(this.selectedChildOrganisation) ? this.selectedParentOrganisation.code
        : this.selectedChildOrganisation.code;
        let organisationId = 
        isNullOrUndefined(this.selectedChildOrganisation) ? this.selectedParentOrganisation.id
        : this.selectedChildOrganisation.id;
        let requestData: RegisterRequest = {
            name: this.typedName,
            username: this.typedUsername,
            email_address: this.typedEmail,
            password: this.typedPassword,
            confirmPassword: this.typedConfirmPassword,
            organisation_code: organisationCode,
            organisation_id: organisationId,
            accessCode: this.typedAccessCode,
            group: "",
            role: ""
        };
        if (!this.hideRoles && this.selectedRoleId !== "") {
            // tslint:disable-next-line:no-string-literal
            requestData["role"] = this.selectedRoleId; 
        }
        if (!this.hideGroups && this.selectedGroupId !== "") {
            // tslint:disable-next-line:no-string-literal
            requestData["group"] = this.selectedGroupId; 
        }
        this.api
            .registerAccount(requestData)
            .subscribe( 
                response => {
                this.decrementAsyncOperations();
                this.registerationCompleted = true;
            },
                error => {
                this.errorMessages.splice(0, this.errorMessages.length);
                this.errorMessages.push(error.message);
                this.decrementAsyncOperations();
                }
            );
            
    }
} 

@observer class Register extends React.Component<{model: RegisterModel, history: BrowserHistory}, {}> {
    componentDidMount() {
        setTimeout(() => {
            this.props.model.fetchOrganisations();    
        },         1000);
        
    }
    render() {
        return (
            
            <div className="container">
                {
                    this.props.model.asyncOperationsCount > 0 &&
                    <CircularProgress centered={true} id="Progress" />
                }
                {this.props.model.asyncOperationsCount === 0 && this.props.model.parentOrganisations.length !== 0 &&
                !this.props.model.registerationCompleted &&
                <form className="formContainer">
                <h1 className="header">Register</h1>
                
                {
                    this.props.model.parentOrganisations.length > 0 &&
                  
                    <SelectField
                        id="parentOrganisation"
                        label="Organisation"
                        placeholder="Select Organisation"
                        required={true}
                        menuItems={
                            this.props.model.parentOrganisations.map(item => {
                                    return {label: item.name, value: item.code};
                            })
                        }
                        defaultValue={this.props.model.parentOrganisations[0].code}
                        value={isNullOrUndefined(this.props.model.selectedParentOrganisation) ? 
                            this.props.model.parentOrganisations[0].code
                            : this.props.model.selectedParentOrganisation.code
                        }
                        onChange={(value) => this.props.model.onParentOrganisationSelected(value.toString())}
                    /> 
             
                } 
                {
                    this.props.model.childOrganisations.length > 0 &&
                    <SelectField
                        id="childOrganisation"
                        required={true}
                        label="Sub Organisation"
                        defaultVisible={true}
                        placeholder="Select Organisation"
                        menuItems={
                            this.props.model.childOrganisations.map(item => {
                                    return {label: item.name, value: item.code};
                            })
                        }
                        defaultValue={this.props.model.childOrganisations[0].code}
                        value={isNullOrUndefined(this.props.model.selectedChildOrganisation) ? 
                            this.props.model.childOrganisations[0].code
                            : this.props.model.selectedChildOrganisation.code
                        }
                        onChange={(value) => this.props.model.onChildOrganisationSelected(value.toString())}
                    />
                }
                <div className="inputContianer">
                {
                    !this.props.model.hideRoles &&
                    <SelectField
                        id="roles"
                        required={true}
                        menuItems={
                            this.props.model.roles.map(item => {
                                    return {label: item.name, value: item.id};
                            })
                        }
                        label="Role"
                        helpText="Select your role."
                        className="stackableTextInputRight"
                        placeholder="Select Role"
                        onChange={(value) => this.props.model.selectedRoleId = value.toString()}
                        disabled={this.props.model.rolesDropdownDisabled}
                    />
                }
                {
                    !this.props.model.hideGroups && 
                    <SelectField
                        id="groups"
                        menuItems={
                            this.props.model.groups.map(item => {
                                    return {label: item.name, value: item.id};
                            })
                        }
                        onChange={(value) => this.props.model.selectedGroupId = value.toString()}
                        helpText="Select your group"
                        required={true}
                        label="Group"
                        className="stackableTextInputLeft"
                        placeholder="Select Group"
                        disabled={this.props.model.groupsDropdownDisabled}
                    />
                }
                
                </div> 
                <div className="inputContianer">
          
                        <TextField
                            id="nameInput"
                            helpText="Min. 3 Characters. Spaces are Allowed."
                            required={true}
                            className="stackableTextInputRight"
                            error={this.props.model.nameError}
                            fullWidth={false}
                            label="Name" 
                            type="text" 
                            onChange={(e) => this.props.model.onTextFieldChange("name", e.toString())}
                            value={this.props.model.typedName}
                        />
                    
                        <TextField
                            id="usernameInput"
                            helpText="Min. 3 Characters. Only letters and numbers."
                            required={true}
                            error={this.props.model.usernameError}
                            className="stackableTextInputLeft"
                            fullWidth={false}
                            label="Username" 
                            type="text" 
                            onChange={(e) => this.props.model.onTextFieldChange("username", e.toString())}
                            value={this.props.model.typedUsername}
                        />
    
                </div>
                <div className="inputContianer">
          
                        <TextField
                            id="passwordInput"
                            helpText="Min. 8 Characters. At least 1 uppercase letter, 1 lowercase letter and 1 number."
                            required={true}
                            className="stackableTextInputRight"
                            error={this.props.model.passwordError}
                            fullWidth={false}
                            label="Password" 
                            type="password" 
                            onChange={(e) => this.props.model.onTextFieldChange("password", e.toString())}
                            value={this.props.model.typedPassword}
                        />
                  
                        <TextField
                            id="confirmPasswordInput"
                            className="stackableTextInputLeft"
                            helpText="Min. 8 Characters. At least 1 uppercase letter, 1 lowercase letter and 1 number."
                            fullWidth={false}
                            label="Confirm Password" 
                            error={this.props.model.confirmPasswordError}
                            required={true}
                            type="password" 
                            onChange={(e) => this.props.model.onTextFieldChange("confirmPassword", e.toString())}
                            value={this.props.model.typedConfirmPassword}
                        />
                  
                </div>
                <div className="inputContianer">
          
                        <TextField
                            id="emailInput"
                            required={ 
                                !isNullOrUndefined(this.props.model.selectedRole)
                                && !isNullOrUndefined(this.props.model.selectedRole.emailOptional) ? 
                                !this.props.model.selectedRole.emailOptional : true 
                            }
                            helpText={
                                (!isNullOrUndefined(this.props.model.selectedRole) &&
                                 !isNullOrUndefined(this.props.model.selectedRole.emailOptional) &&
                                 this.props.model.selectedRole.emailOptional
                                ) ? "Email is optinal for the selected role" :
                                "Must be a valid email address."}
                            className="stackableTextInputRight"
                            fullWidth={false}
                            error={this.props.model.emailError}
                            label="Email" 
                            type="email" 
                            onChange={(e) => this.props.model.onTextFieldChange("email", e.toString())}
                            value={this.props.model.typedEmail}
                        />
                  
                        <TextField
                            id="accessCodeInput"
                            helpText="Optional Access Code if you have one."
                            className="stackableTextInputLeft"
                            fullWidth={false}
                            label="Access Code" 
                            type="text" 
                            onChange={(e) => this.props.model.onTextFieldChange("accessCode", e.toString())}
                            value={this.props.model.typedAccessCode}
                        />
                  
                </div>
                <div className='register-button'>
                <Button
                    className="loginButton margin-top-10" 
                    disabled={this.props.model.registerDisabled}
                    secondary={true}
                    raised={true} 
                    onClick={(e) => {
                        this.props.model.onRegisterClicked();
                        e.preventDefault(); }}
                >
                Register
                </Button>
                </div>
                </form> 
                }
                {
                    this.props.model.asyncOperationsCount === 0 && this.props.model.parentOrganisations.length !== 0 &&
                    !this.props.model.registerationCompleted &&
                    <div className='secondary-option'>
                        <h2>Already have an account?</h2>
                        <div>
                            <Button
                                raised={true}
                                secondary={true}
                                swapTheming={true}
                                className='loginButton'
                                onClick={() => this.props.history.push("/login")}
                            >
                            Log In
                            </Button>
                        </div>
                    </div>
                }
                {
                    this.props.model.asyncOperationsCount === 0 &&
                    this.props.model.registerationCompleted &&
                    <Card className="">
                        <CardTitle 
                            title="Registration Complete" 
                            subtitle="Registeration Form has been submitted successfully" 
                        />
                        <CardText className="">
                            <p>
                                Your details have been submitted successfully.
                                {
                                    isNullOrUndefined(this.props.model.selectedRole.emailOptional) || 
                                    (!isNullOrUndefined(this.props.model.selectedRole) &&
                                    !isNullOrUndefined(this.props.model.selectedRole.emailOptional) &&
                                    !this.props.model.selectedRole.emailOptional) &&
                                    <span>We will process them and send you a verification link on your email.</span>
                                }
                            </p>
                        </CardText>
                        <CardActions className="md-divider-border md-divider-border--top">
                            <Button 
                                flat={true} 
                                secondary={true}
                                onClick={() => this.props.history.push("/login")}
                            >
                            Go To Login
                            </Button>
                        </CardActions>
                    </Card>
                }
                <Snackbar
                    id="errors-snackbar"
                    toasts={this.props.model.errorMessages.map(item => {
                        return {
                            text: item
                        };
                    })}
                    autohide={true}
                    onDismiss={() => {
                        this.props.model.errorMessages = [];
                    }}
                />
                
            </div>
        );

}
    
}

export default withRouter(Register);


// WEBPACK FOOTER //
// ./src/view/Register/Register.tsx